<template>
  <div>
    <spinner v-if="isLoading" class="m-auto mt-64" :width="48" :height="48" />
    <div v-else-if="hasJob && completenessPercentage < 100" data-testid="getting-started">
      <div class="flex justify-between items-end mb-12">
        <h2 class="text-h3">Getting Started</h2>
        <p class="text-h4">{{ completenessPercentage }}% complete</p>
      </div>
      <progress-bar :max="steps.length" :value="completedSteps" class="mb-20" />
      <step-card-list :items="steps" />
      <hr class="my-40 border-grey-divider-subdued" />
    </div>
  </div>
</template>

<script lang="ts">
import ProgressBar from '@/components/progress_bar/ProgressBar.vue';
import StepCardList from '@/components/step_card_list/StepCardList.vue';
import Spinner from '@/components/spinner/Spinner.vue';
import { GETTING_STARTED_REQUESTS_QUERY } from '@/graphql/bookings/queries';
import { Account } from '@/models/account';

export default {
  name: 'GettingStarted',
  components: {
    ProgressBar,
    StepCardList,
    Spinner,
  },
  props: {
    account: {
      required: true,
      type: Account,
    },
  },
  data() {
    return {
      jobInfo: {} as any,
      isLoading: false,
    };
  },
  computed: {
    completedSteps(): number {
      return this.steps.filter((step) => step.variant === 'completedTask').length;
    },
    completenessPercentage(): number {
      return Math.floor((this.completedSteps / this.steps.length) * 100);
    },
    hasJob(): boolean {
      return (
        this.jobInfo &&
        Object.keys(this.jobInfo).length > 0 &&
        new Date(this.jobInfo.expiresAt).toISOString() > new Date().toISOString()
      );
    },
    steps(): Array<any> {
      const isSpecialNeeds = this.jobInfo.careType.toLowerCase() === 'special_needs';
      const maxRate = Math.floor(this.jobInfo.paymentRateMax / 100);
      const maxRateParam = maxRate ? `&rate=${maxRate}` : '';
      const distanceParam = this.jobInfo.distance ? `&distance=${this.jobInfo.distance}` : '';
      const sitterSearchLink = isSpecialNeeds
        ? `/profile/provider/search?search_strategy=special_needs&zipcode=${this.jobInfo.location?.zipcode}`
        : `/availability/search?zipcode=${this.jobInfo.location?.zipcode}${distanceParam}${maxRateParam}&careType=${this.jobInfo.careType.toLowerCase()}`;
      const arr = [
        {
          variant: 'completedTask',
          title: 'Post your needs',
          content: 'Great work! Posting your needs is the first step to finding the perfect sitter.',
          linkURL: null,
          disabled: true,
        },
        {
          variant: this.account.croppedPhoto ? 'completedTask' : 'incompleteTask',
          title: 'Showcase your family',
          content: 'Sitters are more likely to respond to families that have photos.',
          linkURL: this.account.croppedPhoto ? null : '/account/seekers/photo#/upload',
          disabled: !!this.account.croppedPhoto,
          useFullPageNavigation: true,
        },
        {
          variant: this.jobInfo?.hasRecruitedSittersForJob ? 'completedTask' : 'incompleteTask',
          title: 'Invite sitters to apply',
          content: 'Personally invite sitters to apply to your post & fill your needs faster.',
          linkURL: this.jobInfo?.hasRecruitedSittersForJob ? null : sitterSearchLink,
          disabled: this.jobInfo?.hasRecruitedSittersForJob,
          useFullPageNavigation: isSpecialNeeds,
        },
      ];
      return arr.sort((a, b) => !!(b.variant === 'incompleteTask') - !!(a.variant === 'incompleteTask'));
    },
  },
  mounted() {
    this.fetchRequests();
  },
  methods: {
    async fetchRequests() {
      this.isLoading = true;
      try {
        const response = await this.$apollo.query({
          query: GETTING_STARTED_REQUESTS_QUERY,
          variables: { accountId: this.account.id },
        });
        this.jobInfo = response.data.account.jobs.edges[0]?.node;
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
      }
    },
  },
};
</script>
